import { IKnmListFilterRaw } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterRaw';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';

import { IKnmListFilter } from '../interfaces/IKnmListFilter';

export function mapKnmFilterToRaw(data: IKnmListFilter, isErknm: boolean): IKnmListFilterRaw {
  const { supervisionType } = data;

  const getSupervisionTypeId = () => {
    if (isErknm && supervisionType && 'code' in supervisionType) {
      return supervisionType.code;
    }

    return supervisionType?.code ? supervisionType.code : '';
  };

  return {
    name: data.name,
    years: data?.years?.map(yearSelectOption => Number(yearSelectOption.value)) || [],
    domainIds: data?.domains?.map(domain => domain?.id) || [],
    includeDomainChild: data?.includeDomainChild,
    ignoreKno: data?.ignoreKno,
    federalLawIds:
      data?.federalLaws?.map(federalLawSelectOption => Number(federalLawSelectOption?.id)).filter(e => !!e) || [],
    federalLawErvkUuids: isErknm
      ? undefined
      : data?.federalLaws?.map(federalLawSelectOption => String(federalLawSelectOption?.id)).filter(Boolean) || [],
    federalLawName: data.federalLaws?.map(federalLaw => federalLaw.number),
    knmTypeIds: data?.knmTypes?.map(knmTypeSelectOption => knmTypeSelectOption?.id) || [],
    controllingOrganizationIds:
      data?.controllingOrganizations?.map(controllingOrganization =>
        isErknm && 'idStr' in controllingOrganization ? controllingOrganization?.idStr : controllingOrganization?.id,
      ) || [],
    includeChildKno: data?.includeChildKno,
    disapprove: data?.disapprove?.value,
    supervisionTypeId: getSupervisionTypeId(),
    hasViolations: data?.hasViolations?.value,
    dataNotEqualEGRUL: data?.dataNotEqualEGRUL?.value,
    startDate: data?.startDate && formatDate({ date: data.startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    startDateBetween:
      data?.startDateBetweenStart && data?.startDateBetweenEnd
        ? [
            formatDate({ date: data.startDateBetweenStart, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            formatDate({ date: data.startDateBetweenEnd, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
          ]
        : undefined,
    approveDocRequestDate:
      data?.approveDocRequestDate &&
      formatDate({ date: data.approveDocRequestDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    approveDocOrderDate:
      data?.approveDocOrderDate && formatDate({ date: data.approveDocOrderDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    chlistFillingIndicationTypeId: data?.chlistFillingIndicationType?.id,
    subjectInn: data?.subjectInn,
    knmAnnulled: data?.knmAnnulled?.value,
    createdByMe: data?.createdByMe?.value,
    subjectOrgName: data?.subjectOrgName,
    regType: data?.regType?.value,
    planNum: data?.planNum,
    approveDocOrderNum: data?.approveDocOrderNum,
    approveDocRequestNum: data?.approveDocRequestNum,
    objectOgrn: data?.objectOgrn,
    // prosecutorOrganizationIds:
    //   data?.prosecutorOrganizations?.map(prosecutorOrganizationSelectOption =>
    //     Number(prosecutorOrganizationSelectOption.id),
    //   ) || [],
    prosecutorOrganizationErvkUuids:
      data?.prosecutorOrganizations?.map(
        prosecutorOrganizationSelectOption => prosecutorOrganizationSelectOption.ervkUuid,
      ) || [],
    dataSetNotInTime: data?.dataSetNotInTime?.value,
    deletedOnly: data?.deletedOnly?.value,
    deletedInclude: data?.deletedInclude?.value,
    legalBasisDocName: data?.legalBasisDocName,
    orderDone: data?.orderDone?.value,
    recEmptyOrLate: data?.recEmptyOrLate?.value,
    regLate: data?.regLate?.value,
    resultEmpty: data?.resultEmpty?.value,
    resultEmptyOrLate: data?.resultEmptyOrLate?.value,
    appealed: data?.appealed?.value,
    noPunishment: data?.noPunishment?.value,
    approved: data?.approved?.value,
    statusIds: isErknm
      ? data?.statuses?.map(statusSelectOption => statusSelectOption.id) || []
      : data?.statuses?.reduce((dataStatuses: number[], status) => {
          if (status.generalIds) {
            status.generalIds.forEach(item => {
              if (!dataStatuses.includes(item)) {
                dataStatuses.push(item);
              }
            });
          } else {
            dataStatuses.push(status.id);
          }

          return dataStatuses;
        }, []),
    signed: data?.signed?.value,
    pubStatuses: data?.pubStatuses?.map(pubStatusSelectOption => pubStatusSelectOption.value) || [],
    subjectTypeCodes: data?.subjectTypes?.map(subjectTypeSelectOption => subjectTypeSelectOption.code) || [],
    notifyLate: data?.notifyLate?.value,
    admLevelErvkId: data?.admLevel?.id,
    erpId: data?.erpId,
    inspectorFullName: data?.inspectorFullName,
    address: data.address,
    createPlanDate:
      data?.createPlanDate && formatDate({ date: data.createPlanDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    updatePlanDate:
      data?.updatePlanDate && formatDate({ date: data.updatePlanDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    ...(isErknm && { kinds: data.kinds?.map(item => item.label) }),
    mspCategory: data?.mspCategory?.title ? [data?.mspCategory?.title] : undefined,
    approveRequiredType: data.approveRequiredType?.value,
    approvedRequired: data.approvedRequired?.value,
    version: isErknm ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP,
    requirementDetails: data?.requirementDetails,
    dateRequirement: data?.dateRequirement,
    assignmentNumber: data?.assignmentNumber,
    assignmentDate:
      data?.assignmentDate && formatDate({ date: data.assignmentDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
    assignmentDateBetween:
      data?.assignmentDateBetweenStart && data?.assignmentDateBetweenEnd
        ? [
            formatDate({ date: data.assignmentDateBetweenStart, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            formatDate({ date: data.assignmentDateBetweenEnd, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
          ]
        : undefined,
    responsibilityTypeCodes:
      data?.responsibilityTypeCodes?.map(
        responsibilityTypeCodesSelectOption => responsibilityTypeCodesSelectOption.id,
      ) || [],
    amountOfPunishmentBetween:
      data?.amountOfPunishmentFrom && data?.amountOfPunishmentTo
        ? [data?.amountOfPunishmentFrom, data?.amountOfPunishmentTo]
        : undefined,
    isTest: data?.isTest?.value,
  };
}
